import {
  useEffect,
  type Dispatch,
  type SetStateAction,
} from "react";

export type ManualActionsAntdSortOrder = "ascend" | "descend" | null;

/** Maps Ant Design table `order` to API `sort_order` (matches Wallet / SportsBets). */
export function sortOrderToApiParam(
  order: ManualActionsAntdSortOrder | undefined,
): "asc" | "desc" | undefined {
  if (order === "ascend") return "asc";
  if (order === "descend") return "desc";
  return undefined;
}

/** PAM v2 list sort: `+field` / `-field`. Omit when the column is unsorted. */
export function toAalSort(
  field: string | null | undefined,
  order: ManualActionsAntdSortOrder | "asc" | "desc" | undefined,
): string | undefined {
  if (!field) return undefined;
  if (order === "descend" || order === "desc") return `-${field}`;
  if (order === "ascend" || order === "asc") return `+${field}`;
  return undefined;
}

/** Normalize `SorterResult.field`, including array-shaped fields from nested columns. */
export function normalizeAntdSortField(field: unknown): string | null {
  if (field === undefined || field === null) return null;
  if (Array.isArray(field)) {
    const first = field[0];
    return first !== undefined && first !== null ? String(first) : null;
  }
  return String(field);
}

export function manualActionsQueryLoading(
  isLoading: boolean,
  isFetching: boolean,
): boolean {
  return isLoading || isFetching;
}

type SorterLike = {
  field?: unknown;
  order?: unknown;
};

export function applyAntdSorterToSortState(
  sorter: unknown,
  setSortField: (field: string | null) => void,
  setSortOrder: (order: ManualActionsAntdSortOrder) => void,
  resolveField: (field: unknown) => string | null = normalizeAntdSortField,
): void {
  const s = (Array.isArray(sorter) ? sorter[0] : sorter) as SorterLike | undefined;
  const field = resolveField(s?.field);
  if (field) {
    setSortField(field);
    const order = s?.order;
    setSortOrder(order === "ascend" || order === "descend" ? order : null);
  } else {
    setSortField(null);
    setSortOrder(null);
  }
}

/** Reset APV role, sort, and page when switching between agent performance view and the panel (Wallet / SportsBets pattern). */
export function useResetManualActionsTableOnApvToggle(
  agentPerformanceView: boolean,
  setSelectedRole: Dispatch<SetStateAction<string[]>>,
  setSortField: Dispatch<SetStateAction<string | null>>,
  setSortOrder: Dispatch<SetStateAction<ManualActionsAntdSortOrder>>,
  setCurrentPage: Dispatch<SetStateAction<number>>,
): void {
  useEffect(() => {
    if (!agentPerformanceView) {
      setSelectedRole([]);
    }
    setSortField(null);
    setSortOrder(null);
    setCurrentPage(1);
    // Match Wallet / SportsBets: only re-run when the view mode toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentPerformanceView]);
}

export function useResetManualActionsPageOnApvRoleChange(
  agentPerformanceView: boolean,
  selectedRole: string[],
  setCurrentPage: Dispatch<SetStateAction<number>>,
): void {
  useEffect(() => {
    if (agentPerformanceView) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, agentPerformanceView]);
}
